import React, { useEffect, useState } from "react";
import { api, dateFR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, BadgeCheck, KeyRound, Pencil } from "lucide-react";

const empty = { email: "", password: "", name: "", role: "commercial" };

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  // Password change dialog
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");

  // Rename dialog
  const [nmOpen, setNmOpen] = useState(false);
  const [nmTarget, setNmTarget] = useState(null);
  const [newName, setNewName] = useState("");

  const load = async () => {
    const { data } = await api.get("/users");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Utilisateur créé");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur");
    }
  };

  const openPwDialog = (u) => {
    setPwTarget(u);
    setNewPw("");
    setPwOpen(true);
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (!pwTarget) return;
    if (newPw.length < 6) {
      toast.error("Mot de passe trop court (min 6 caractères)");
      return;
    }
    try {
      await api.put(`/users/${pwTarget.id}/password`, { new_password: newPw });
      toast.success(`Mot de passe modifié pour ${pwTarget.email}`);
      setPwOpen(false);
      setPwTarget(null);
      setNewPw("");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur");
    }
  };

  const openNmDialog = (u) => {
    setNmTarget(u);
    setNewName(u.name || "");
    setNmOpen(true);
  };

  const submitNm = async (e) => {
    e.preventDefault();
    if (!nmTarget) return;
    if (!newName.trim()) {
      toast.error("Le nom ne peut pas être vide");
      return;
    }
    try {
      await api.put(`/users/${nmTarget.id}`, { name: newName.trim() });
      toast.success("Nom modifié et mis à jour partout (ventes, tâches)");
      setNmOpen(false);
      setNmTarget(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer ${u.email} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Administration
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Utilisateurs</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Gérez les comptes administrateur et commercial. Les commerciaux ne voient pas les agrégats financiers (CA, encaissé, créances).
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="user-add-btn" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
              <Plus size={14} className="mr-1" /> Nouvel utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display tracking-tight">Nouvel utilisateur</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nom *</Label>
                <Input data-testid="user-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Email *</Label>
                <Input data-testid="user-form-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Mot de passe *</Label>
                <Input data-testid="user-form-password" type="text" minLength={6} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm" />
                <p className="text-xs text-muted-foreground mt-1">Min. 6 caractères · Communiquez-le au nouvel utilisateur.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Rôle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-form-role" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commercial">Commercial (sans accès au CA)</SelectItem>
                    <SelectItem value="atelier">Atelier / Mécanicien</SelectItem>
                    <SelectItem value="admin">Administrateur (accès total)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="user-form-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                  Créer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Nom</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Rôle</th>
              <th className="px-6 py-3">Créé le</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table-body">
            {items.map((u) => {
              return (
                <tr key={u.id} className="border-t hover:bg-slate-50">
                  <td className="px-6 py-3 font-medium">{u.name}</td>
                  <td className="px-6 py-3">{u.email}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider ${
                      u.role === "admin" ? "bg-klein/10 text-klein border border-klein/30"
                      : u.role === "atelier" ? "bg-amber-50 text-amber-700 border border-amber-200"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}>
                      {u.role === "admin" ? <ShieldCheck size={12} /> : <BadgeCheck size={12} />}
                      {u.role === "admin" ? "Admin" : u.role === "atelier" ? "Atelier" : "Commercial"}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-muted-foreground">{dateFR(u.created_at)}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => openNmDialog(u)}
                        data-testid={`user-rename-${u.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-klein hover:bg-klein/10 rounded-sm"
                        title="Renommer"
                      >
                        <Pencil size={12} /> Renommer
                      </button>
                      <button
                        onClick={() => openPwDialog(u)}
                        data-testid={`user-change-pw-${u.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-klein hover:bg-klein/10 rounded-sm"
                        title="Changer le mot de passe"
                      >
                        <KeyRound size={12} /> Mot de passe
                      </button>
                      {u.id !== user?.id && (
                        <button onClick={() => remove(u)} data-testid={`user-delete-${u.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-red-50 rounded-sm">
                          <Trash2 size={12} /> Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rename dialog */}
      <Dialog open={nmOpen} onOpenChange={setNmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Renommer l'utilisateur</DialogTitle>
          </DialogHeader>
          {nmTarget && (
            <form onSubmit={submitNm} className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Pour <span className="font-semibold text-foreground">{nmTarget.email}</span>
              </p>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nouveau nom *</Label>
                <Input
                  data-testid="user-rename-input"
                  required
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="mt-1 rounded-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Le nom sera mis à jour partout : ventes (commercial), tâches assignées, Bilan.
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  data-testid="user-rename-submit"
                  className="bg-klein hover:bg-blue-800 text-white rounded-sm"
                >
                  Renommer
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Password change dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Nouveau mot de passe
            </DialogTitle>
          </DialogHeader>
          {pwTarget && (
            <form onSubmit={submitPw} className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Pour <span className="font-semibold text-foreground">{pwTarget.email}</span>
              </p>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nouveau mot de passe *</Label>
                <Input
                  data-testid="user-pw-new"
                  type="text"
                  minLength={6}
                  required
                  autoFocus
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  className="mt-1 rounded-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Min. 6 caractères · Communiquez-le directement à l'utilisateur.
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  data-testid="user-pw-submit"
                  className="bg-klein hover:bg-blue-800 text-white rounded-sm"
                >
                  Mettre à jour
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
